/**
 * The `ws:fanout` envelope — the contract between the WebSocket gateway and every process that
 * needs to reach a socket it does not hold.
 *
 * WHY THIS LIVES IN @hrms/shared RATHER THAN IN apps/ws. Three processes participate: the gateway
 * consumes, while the worker (`@hrms/notify` delivering an in-app notification) and the AI runtime
 * (streaming tokens) both produce. With the schema defined inside the consumer, a producer can only
 * copy the shape by hand — and the first time the two copies disagree, the gateway silently
 * discards the envelope as malformed. The symptom is "notifications stopped arriving", with nothing
 * broken anywhere anyone would look.
 *
 * Defining it once, here, makes that disagreement a typecheck failure instead.
 *
 * THE PAYLOAD IS A DISCRIMINATED UNION, not `Record<string, unknown>`. The looser form compiles
 * everywhere and pushes the real shape into a cast at the emit site, which is exactly where a
 * mismatch stops being checkable. A union means adding an event forces every producer and the
 * consumer to handle it.
 */

import { z } from 'zod';
import { uuidSchema } from '../schemas/common.js';
import { chatReplyToSchema } from './chat.contract.js';

/** The Redis pub/sub channel. One channel, many event types, discriminated by `event`. */
export const FANOUT_CHANNEL = 'ws:fanout';

/**
 * Where the envelope is delivered.
 *
 * Deliberately NOT a room name. `orgId` is what scopes the room, and the gateway derives the name
 * itself — accepting a room string from the channel would put cross-tenant delivery one typo in
 * the worker away from happening, with no validation able to catch it.
 */
export const fanoutTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('person'), personId: uuidSchema }),
  z.object({ kind: z.literal('conversation'), convId: uuidSchema }),
]);
export type FanoutTarget = z.infer<typeof fanoutTargetSchema>;

/** A chat message as the gateway puts it on the wire (`apps/ws/src/lib/serialize.ts`). */
export const fanoutChatMessageSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  seq: z.string(),
  senderId: uuidSchema.nullable(),
  kind: z.string(),
  body: z.string().nullable(),
  parentId: uuidSchema.nullable(),
  /** The quoted parent when this is a reply (see `chatReplyToSchema` / `buildReplyTo`). Optional so
   * producers that never emit replies (approval cards) need not set it. */
  replyTo: chatReplyToSchema.nullable().optional(),
  clientMsgId: z.string().nullable(),
  meta: z.unknown(),
  editedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
  createdAt: z.string(),
  attachments: z.array(
    z.object({
      id: uuidSchema,
      fileName: z.string(),
      mimeType: z.string(),
      sizeBytes: z.number().int().nonnegative(),
    }),
  ),
});

export const fanoutEnvelopeSchema = z.discriminatedUnion('event', [
  /**
   * Chat messages that are created by a process other than the gateway — today, approval cards
   * posted by the API when a workflow step activates. Targeted at a PERSON so it reaches them
   * whether or not they have that conversation open.
   */
  z.object({
    event: z.literal('message:new'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    payload: fanoutChatMessageSchema,
  }),
  z.object({
    event: z.literal('message:updated'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    payload: fanoutChatMessageSchema,
  }),
  z.object({
    event: z.literal('notification:new'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    payload: z.object({
      id: uuidSchema,
      key: z.string().min(1),
      title: z.string(),
      body: z.string().optional(),
      /** Deep link the client opens on tap. Relative, so it works on web and mobile alike. */
      href: z.string().optional(),
      createdAt: z.string().datetime({ offset: true }),
    }),
  }),
  /**
   * A person muted or unmuted a conversation from one device. Targeted at a PERSON (not the
   * conversation room) so it reaches their other open tabs/devices even if none of them currently
   * has that conversation open — the same reason a desktop notification needs to stop immediately.
   */
  z.object({
    event: z.literal('conversation:muted'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    payload: z.object({
      conversationId: uuidSchema,
      mutedUntil: z.string().nullable(),
    }),
  }),
  /**
   * A person left a conversation or was removed from it. Targeted at a PERSON so every one of
   * their sockets drops the conversation room immediately — before this, `leftAt` was set in the
   * database but their open sockets kept receiving the room's events until they reconnected.
   */
  z.object({
    event: z.literal('conversation:member_removed'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    payload: z.object({ conversationId: uuidSchema }),
  }),
  /**
   * A person hid a message "for me" on one device. Targeted at that PERSON so their other
   * tabs/devices drop it too; nobody else is told (the message is unchanged for them).
   */
  z.object({
    event: z.literal('message:hidden'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    payload: z.object({ conversationId: uuidSchema, messageId: uuidSchema }),
  }),

  z.object({
    event: z.literal('ai:delta'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    payload: z.object({
      /** Correlates the deltas, the terminal event, and the persisted message. */
      streamId: uuidSchema,
      convId: uuidSchema.optional(),
      delta: z.string(),
    }),
  }),
  z.object({
    event: z.literal('ai:done'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    payload: z.object({
      streamId: uuidSchema,
      convId: uuidSchema.optional(),
      /** Set once the assistant turn has been persisted, so the client can reconcile. */
      messageId: uuidSchema.optional(),
    }),
  }),
  z.object({
    event: z.literal('ai:error'),
    orgId: uuidSchema,
    target: fanoutTargetSchema,
    /** User-facing text only. An internal error must never reach a socket verbatim. */
    payload: z.object({ streamId: uuidSchema, message: z.string() }),
  }),
]);

export type FanoutEnvelope = z.infer<typeof fanoutEnvelopeSchema>;
export type FanoutEvent = FanoutEnvelope['event'];

/**
 * Minimal shape of whatever Redis client the caller holds.
 *
 * Structural rather than an ioredis import, because this package is consumed by React Native —
 * pulling a Node-only client in here would break the mobile bundle for the sake of one method.
 */
export interface FanoutPublisher {
  publish(channel: string, message: string): Promise<unknown>;
}

/**
 * Publish an envelope. Validates before sending, so a malformed payload fails in the producer —
 * where the stack trace points at the bug — rather than being discarded by the gateway, where the
 * only evidence is a log line in a different service.
 */
export async function publishFanout(
  redis: FanoutPublisher,
  envelope: FanoutEnvelope,
): Promise<void> {
  const parsed = fanoutEnvelopeSchema.parse(envelope);
  await redis.publish(FANOUT_CHANNEL, JSON.stringify(parsed));
}
